import React, { forwardRef, useRef } from "react";
import { Clock, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const WarningModal = forwardRef(function WarningModal(
  { countdown, onLogout, onStaySignedIn, locked, buttonDisable },
  ref
) {
  // The modal remounts each time the warning appears, so the first countdown
  // we see is the full window. Track the max so the progress bar can size
  // itself without the total being passed in.
  const maxRef = useRef(countdown);
  if (countdown > maxRef.current) maxRef.current = countdown;
  const total = maxRef.current || 1;

  const safe = Math.max(0, countdown);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  const timeText = `${minutes}:${String(seconds).padStart(2, "0")}`;
  const pct = Math.max(0, Math.min(100, (safe / total) * 100));

  const isUrgent = safe <= 60;
  const renewing = buttonDisable && !locked;

  return (
    <div
      ref={ref}
      // Above every dialog tier. This is mounted globally and is not a Radix dialog, so it takes no part
      // in their stacking: at 1060 it rendered UNDER an open dialog's backdrop, and a user who went idle
      // with a dialog open was logged out having never seen the countdown. pointer-events-auto because
      // Radix sets pointer-events:none on body while a modal is open, which would leave the Stay button
      // visible but dead. See the scale in components/ui/dialog.jsx.
      className="tw:fixed tw:inset-0 tw:z-[1300] tw:pointer-events-auto tw:flex tw:items-center tw:justify-center tw:bg-slate-900/60 tw:backdrop-blur-sm tw:p-4"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-desc"
        className="tw:w-full tw:max-w-[400px] tw:overflow-hidden tw:rounded-xl tw:bg-white tw:shadow-2xl"
      >
        <div className="tw:px-7 tw:pt-7 tw:pb-2">
          <div
            className={cn(
              "tw:mb-4 tw:flex tw:h-11 tw:w-11 tw:items-center tw:justify-center tw:rounded-xl",
              isUrgent
                ? "tw:bg-red-100 tw:text-red-600"
                : "tw:bg-amber-100 tw:text-amber-600"
            )}
          >
            <Clock size={24} strokeWidth={2} />
          </div>

          <div
            id="session-timeout-title"
            className="tw:text-lg tw:font-semibold tw:text-slate-900"
          >
            Are you still there?
          </div>
          <div
            id="session-timeout-desc"
            className="tw:mt-1 tw:text-sm tw:text-slate-500"
          >
            For your security, we&rsquo;ll sign you out soon due to inactivity.
          </div>

          <div className="tw:mt-5 tw:flex tw:items-baseline tw:justify-between">
            <span
              className={cn(
                "tw:text-4xl tw:font-semibold tw:leading-none tw:tabular-nums",
                isUrgent ? "tw:text-red-600" : "tw:text-slate-900"
              )}
            >
              {timeText}
            </span>
            <span className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-slate-400">
              remaining
            </span>
          </div>
          <div className="tw:mt-2.5 tw:h-1.5 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-slate-100">
            <div
              className={cn(
                "tw:h-full tw:rounded-full tw:transition-all tw:duration-1000 tw:ease-linear",
                isUrgent ? "tw:bg-red-600" : "tw:bg-brand-secondary"
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="tw:px-7 tw:pb-6 tw:pt-5">
          {locked ? (
            <div className="tw:flex tw:items-center tw:gap-2.5 tw:text-sm tw:text-slate-500">
              <ShieldCheck size={18} />
              Your session is ending&hellip;
            </div>
          ) : renewing ? (
            <div className="tw:flex tw:items-center tw:gap-2.5 tw:text-sm tw:font-medium tw:text-slate-500">
              <Loader2
                size={18}
                className="tw:animate-spin tw:motion-reduce:animate-none"
              />
              Keeping you signed in&hellip;
            </div>
          ) : (
            <div className="tw:flex tw:gap-2.5">
              <Button
                variant="outline"
                className="tw:flex-1"
                onClick={onLogout}
              >
                Log out
              </Button>
              <Button
                variant="secondary"
                className="tw:flex-1"
                onClick={onStaySignedIn}
                autoFocus
              >
                Stay signed in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default WarningModal;